import { Component, EventEmitter, OnInit, Output, Input, OnChanges, inject } from '@angular/core';
import { MultiSubscriptionComponent } from '@ddv/common-components';
import { DashboardExportService, DashboardPDFExportService } from '@ddv/dashboards';
import { CrosstalkDataService, Datasource, MetadataService, WidgetDataSourceService } from '@ddv/datasets';
import { UserService } from '@ddv/entitlements';
import { Workspace, ManagerService } from '@ddv/layout';
import {
    WIDGET_LIFECYCLE_EVENT,
    crosstalkCheckboxFieldId,
    DdvDate,
    DdvDateTime,
    ExportDatasetInfo,
    ExportDatasetWidgetInfo,
    ExportInfo,
    ExportWidgetInfo,
    ExportFormat,
    FieldMetadata,
    MetadataLookup,
    UserPreferences,
    UserDefinedFieldType,
    ValueConfigItem,
    WidgetData,
    WidgetIdNamedQueryId,
    GridConfigItem,
} from '@ddv/models';
import { deepClone } from '@ddv/utils';
import { FilteredDataService, WidgetExportService, ExportableData } from '@ddv/widgets';
import { Observable, switchMap, tap } from 'rxjs';

import { ExportRequestParameter } from '../../models/export-request-parameter';
import { ExportType } from '../../models/export-type';
import { UdfColumn } from '../../models/udf-column';

@Component({
    selector: 'app-dashboard-export',
    templateUrl: 'dashboard-export.component.html',
    styleUrls: ['dashboard-export.component.scss'],
    standalone: false,
})
export class DashboardExportComponent extends MultiSubscriptionComponent implements OnInit, OnChanges {
    @Input() userPreferences: UserPreferences | undefined;

    @Output() exportOptionsClose = new EventEmitter<ExportInfo>();

    private readonly dashboardPDFExportService = inject(DashboardPDFExportService);
    private readonly filteredDataService = inject(FilteredDataService);
    private readonly managerService = inject(ManagerService);
    private readonly metadataService = inject(MetadataService);
    private readonly widgetDataSourceService = inject(WidgetDataSourceService);
    private readonly userService = inject(UserService);
    private readonly exportService = inject(WidgetExportService);
    private readonly crosstalkDataService = inject(CrosstalkDataService);
    private readonly dashboardExportService = inject(DashboardExportService);

    isFullExport = false;
    selectedWidgetCount = 0;
    includeCrosstalkKeys = false;

    protected selectAll = { isChecked: false, value: false };
    protected showCrosstalkKeysExportToggle = false;
    // These are just a data-struct pulled from various places that give the inputs you need to collect all the data to export
    protected exportWidgetInfos: ExportWidgetInfo[] = [];
    protected isDataLoaded = false;

    private currentDashboard: Workspace | undefined;
    private exportFormat: ExportFormat | undefined;
    private exportInfo: ExportInfo | undefined;
    private metadata: MetadataLookup | { [widgetId: number | string]: MetadataLookup | undefined } | undefined;
    private requestParam: ExportRequestParameter | undefined;
    private selectedWidgetInfos: ExportWidgetInfo[] = [];
    private gridsSelectedWidgetsInfos: ExportWidgetInfo[] = [];
    private nonGridsSelectedWidgetsInfos: ExportWidgetInfo[] = [];
    private sequenceNo = 1;
    private gridsFullExportColumns: { widgetId: string, columns: string[] | undefined }[] | undefined;
    private gridsMetadata: MetadataLookup | { [widgetId: number | string]: MetadataLookup | undefined } | undefined;
    private nonGridsMetadata: MetadataLookup | { [widgetId: number | string]: MetadataLookup | undefined } | undefined;
    private widgetDatasource: Datasource | undefined;

    ngOnInit(): void {
        this.subscribeTo(this.widgetDataSourceService.dataSource$, (dataSource) => {
            this.widgetDatasource = dataSource;
            this.setIsDataLoaded();
        });

        this.currentDashboard = this.managerService.getWorkspace();
        this.exportWidgetInfos = this.managerService.getAppWidgetsState()
            .map((widget) => {
                return {
                    id: widget.id ?? 0,
                    isSubscribedToDashboardFilters: !!widget.isSubscribedToDashboardFilters,
                    name: (widget.displayNameType === 'CUSTOM' ? widget.customDisplayName : widget.name) ?? '',
                    isChecked: false,
                    namedQuery: widget.namedQuery!,
                    namedQueryId: widget.namedQueryId,
                    currentVisualization: widget.currentVisualization,
                    currentVisualizationConfig: widget.visualizationConfigs.find((config) => {
                        return config.visualizationType === widget.currentVisualization;
                    }),
                };
            })
            .sort((a, b) => (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()));

        this.showCrosstalkKeysExportToggle = !!this.currentDashboard?.extraParameters?.widgets.some((widget) => {
            return widget.namedQuery?.normalizeConversableType() && widget.currentVisualization === 'ADVANCED_GRID';
        });

        this.exportService.updateIncludeCrosstalkKeys(false);
    }

    ngOnChanges(): void {
        this.isFullExport = !!this.userPreferences?.isFullExport;
    }

    async exportDashboard(exportFormat: ExportFormat): Promise<void> {
        this.exportFormat = exportFormat;
        this.setExportInfo();
        this.sequenceNo = 1;
        this.selectedWidgetInfos = this.exportWidgetInfos.filter((ew) => ew.isChecked);

        if (!this.selectedWidgetInfos.length && this.exportFormat === 'PDF') {
            const widgets = this.exportWidgetInfos.map((widget) => ({
                widgetName: widget.name,
                widgetId: widget.id.toString(),
                columnNames: [],
            }));
            this.requestParam = this.getExportRequestParams(undefined);
            this.requestParam.dataSet.push({ metadata: undefined, widgets, data: [], summary: undefined });
            await this.exportData();
        }

        if (!this.selectedWidgetInfos.length) {
            this.sendNoWidgetMessage();
            return;
        }

        this.gridsSelectedWidgetsInfos = this.selectedWidgetInfos.filter((widget) => {
            return widget.currentVisualization?.includes('GRID');
        });

        if (this.isFullExport) {
            this.nonGridsSelectedWidgetsInfos = this.selectedWidgetInfos.filter((widget) => {
                return !widget.currentVisualization?.includes('GRID');
            });

            let gridContexts: WidgetIdNamedQueryId[] | undefined;
            let nonGridContexts: WidgetIdNamedQueryId[] | undefined;

            if (this.gridsSelectedWidgetsInfos.length) {
                gridContexts = this.exportInfosToMetadataFetchContexts(this.gridsSelectedWidgetsInfos);
            }

            if (this.nonGridsSelectedWidgetsInfos.length) {
                nonGridContexts = this.exportInfosToMetadataFetchContexts(this.nonGridsSelectedWidgetsInfos);
            }

            this.selectedWidgetInfos.forEach((selectedWidget) => {
                this.managerService.sendMessageToExistingWidget(selectedWidget.id, WIDGET_LIFECYCLE_EVENT.EXPORT_FULL_DATA);
            });

            this.requestParam = {
                dashboardName: this.currentDashboard?.getExtraParameters()?.name ?? '',
                dashboardId: this.currentDashboard?.id.toString() ?? '',
                dataSet: [],
            };

            if (gridContexts && !nonGridContexts) {
                this.subscribeTo(this.metadataService.fetchAllMetadata(gridContexts)
                    .pipe(
                        tap((gridsMetadata) => {
                            this.gridsMetadata = gridsMetadata;
                        }),
                        switchMap(() => this.filteredDataService.fullData$)), async (fullData) => this.exportFullDashboard(fullData));
            }

            if (!gridContexts && nonGridContexts) {
                this.subscribeTo(this.metadataService.fetchAllMetadata(nonGridContexts)
                    .pipe(
                        tap((nonGridsMetadata) => {
                            this.nonGridsMetadata = nonGridsMetadata;
                        }),
                        switchMap(() => this.filteredDataService.fullData$)), async (fullData) => this.exportFullDashboard(fullData));
            }

            if (gridContexts && nonGridContexts) {
                this.subscribeTo(this.metadataService.fetchAllMetadata([...gridContexts, ...nonGridContexts])
                    .pipe(
                        tap((metadata) => {
                            this.gridsMetadata = {};
                            this.nonGridsMetadata = {};
                            for (const widgetId in metadata) {
                                if (gridContexts?.find((gc) => gc.widgetId?.toString() === widgetId)) {
                                    this.gridsMetadata = {
                                        ...this.gridsMetadata,
                                        [widgetId]: metadata[widgetId],
                                    } as { [widgetId: number | string]: MetadataLookup | undefined };
                                } else if (nonGridContexts?.find((gc) => gc.widgetId?.toString() === widgetId)) {
                                    this.nonGridsMetadata = {
                                        ...this.nonGridsMetadata,
                                        [widgetId]: metadata[widgetId],
                                    } as { [widgetId: number | string]: MetadataLookup | undefined };
                                }
                            }
                        }),
                        switchMap(() => this.filteredDataService.fullData$)), async (fullData) => this.exportFullDashboard(fullData),
                );
            }
        } else {
            this.subscribeTo(this.getFilteredData(), async (filteredData) => this.exportFilteredDashboard(filteredData));
        }
    }

    onExportTypeChange(isFilteredExport: boolean): void {
        this.isFullExport = !isFilteredExport;

        if (this.userPreferences) {
            this.userPreferences.isFullExport = this.isFullExport;
            this.userService.updateUserPreferenceData(undefined, this.userPreferences).subscribe();
        }
    }

    onCrosstalkKeyExportChange(excludeCrosstalkKeys: boolean): void {
        this.includeCrosstalkKeys = !excludeCrosstalkKeys;
        this.exportService.updateIncludeCrosstalkKeys(!excludeCrosstalkKeys);
    }

    async exportData(): Promise<void> {
        if (!this.requestParam || !this.exportInfo || !this.exportFormat) {
            // On theory, we will never get into this if statement, but just in case
            // Additionally, it helps eliminate the need for the type assertions that were previously used.
            this.exportOptionsClose.emit();
            console.error('Export data is unavailable.');
            return;
        }

        const exportType = this.isFullExport ? ExportType.FULL : ExportType.FILTERED;
        const datasets = this.requestParam.dataSet.map((dataset) => {
            if (dataset.crosstalkFields) {
                return setExportDatasetInfoForExportWithCrosstalkKeys(dataset);
            }
            return dataset;
        });

        this.exportInfo.isExportInitialized = true;
        this.exportInfo.message = `${this.requestParam.dashboardName}, ${this.exportFormat} has been initiated.`;

        this.moveConversationIdAsLastColumn();

        if (this.exportFormat === 'PDF') {
            await this.exportDashboardToPDF();
        } else if (exportType === ExportType.FULL && (this.exportFormat === 'CSV' || this.exportFormat === 'EXCEL')) {
            this.fullExport(this.requestParam.dashboardName, datasets, this.exportFormat);
        } else {
            this.filteredExport(this.requestParam.dashboardName, datasets, this.exportFormat);
        }
        this.exportOptionsClose.emit();
    }

    onSelectChange(item: ExportWidgetInfo, isChecked: boolean): void {
        const info = this.exportWidgetInfos?.find((widget) => widget.id === item.id);
        if (info) {
            info.isChecked = isChecked;
        }
        this.selectedWidgetCount = this.exportWidgetInfos.filter((widget) => widget.isChecked).length;
        this.selectAll.isChecked = this.selectedWidgetCount === this.exportWidgetInfos.length;
    }

    onSelectAllChange(isChecked = false): void {
        this.exportWidgetInfos.forEach((widget) => { widget.isChecked = isChecked; });
        this.selectedWidgetCount = isChecked ? this.exportWidgetInfos.length : 0;
    }

    async exportDashboardToPDF(): Promise<void> {
        const dashboardName = this.managerService.getWorkspace()?.name;
        await this.dashboardPDFExportService.exportDashboardToPDF(`${dashboardName}_${DdvDate.today.toDDMMYYYYFormat()}`);
    }

    private getFilteredData(): Observable<Record<string, ExportableData>> {
        const contexts: WidgetIdNamedQueryId[] = this.exportInfosToMetadataFetchContexts(this.selectedWidgetInfos);

        return this.metadataService.fetchAllMetadata(contexts)
            .pipe(
                tap((metadata) => this.sendMessageToExistingWidgets(metadata)),
                switchMap(() => this.filteredDataService.filteredData$));
    }

    private sendMessageToExistingWidgets(metadata: Record<number, MetadataLookup | undefined>): void {
        this.metadata = metadata;

        this.selectedWidgetInfos.forEach((widget) => {
            const eventType = this.getExportLifecycleEvent(widget);
            this.managerService.sendMessageToExistingWidget(widget.id, eventType);
        });
    }

    private getExportLifecycleEvent(widget: ExportWidgetInfo): WIDGET_LIFECYCLE_EVENT {
        return this.exportFormat === 'CSV' && widget.currentVisualization === 'ADVANCED_GRID' ?
            WIDGET_LIFECYCLE_EVENT.EXPORT_FILTERED_ADVANCED_GRID_DATA_TO_CSV :
            WIDGET_LIFECYCLE_EVENT.EXPORT_FILTERED_DATA;
    }

    private setIsDataLoaded(): void {
        this.isDataLoaded = !!(this.widgetDatasource && this.widgetDatasource.datasources.length > 0);
    }

    private setExportInfo(): void {
        this.currentDashboard = this.managerService.getWorkspace();
        this.exportInfo = {
            dashboardName: this.currentDashboard?.getExtraParameters()?.name,
            isExportInitialized: false,
            exportFormat: this.exportFormat,
        };
    }

    private sendNoWidgetMessage(): void {
        if (this.exportInfo) {
            this.exportInfo.message = 'No widgets available for export.';
        }
        this.exportOptionsClose.emit();
    }

    private getSubscribedWidgetDatasets(
        metadata: { [widgetId: number | string]: MetadataLookup },
        isGrid: boolean,
        exportData?: Record<string, ExportableData>,
    ): ExportDatasetInfo[] {
        if (!this.isDataLoaded) {
            throw new Error('Cannot export - data sources have not loaded');
        }

        const exportDatasetInfos: ExportDatasetInfo[] = [];
        const selectedWidgetInfos = isGrid ? this.gridsSelectedWidgetsInfos : this.nonGridsSelectedWidgetsInfos;
        selectedWidgetInfos.forEach((widgetInfo) => {
            const widgetColumns = this.getWidgetColumns(widgetInfo.currentVisualizationConfig?.configs?.values);
            const dataTypeOverrides = this.getDataTypeOverrides(widgetInfo, metadata);

            if (!widgetInfo.isSubscribedToDashboardFilters) {
                return;
            }

            let udfColumns: { colId: string | undefined, userDefinedFieldType: UserDefinedFieldType | undefined }[] = [];
            if (widgetInfo.currentVisualization === 'ADVANCED_GRID') {
                udfColumns = widgetInfo.currentVisualizationConfig?.configs?.values
                    .filter((column) => this.exportService.isUserDefinedFieldColumn(column.value))
                    .map((column) => {
                        return { colId: column.value, userDefinedFieldType: column.datatype as UserDefinedFieldType };
                    }) ?? [];
            }
            const namedQueryId = widgetInfo.namedQueryId ?? '';
            let data = this.getWidgetData('dashboard', namedQueryId, udfColumns);
            const columns = this.getColumns(widgetInfo, isGrid, widgetColumns, metadata);
            const widgets = this.getWidgets(widgetInfo, columns, exportData ?? {}, dataTypeOverrides);

            let crosstalkFields: { name: string, displayName: string }[] | undefined;
            if (this.exportService.shouldIncludeCrosstalkKeys(widgetInfo)) {
                data = this.addCrosstalkKeysData(widgetInfo, data);
                crosstalkFields = this.crosstalkDataService.getCrosstalkSchemaFields(namedQueryId);
            }
            exportDatasetInfos.push({ metadata: metadata[widgetInfo.id], widgets, data, summary: {}, crosstalkFields });

            this.sequenceNo += 1;
        });
        return exportDatasetInfos;
    }

    private getUnsubscribedWidgetDatasets(
        metadata: { [widgetId: number | string]: MetadataLookup },
        isGrid: boolean,
        exportData?: Record<string, ExportableData>,
    ): ExportDatasetInfo[] {
        if (!this.isDataLoaded) {
            throw new Error('Cannot export - data sources have not loaded');
        }

        const exportDatasetInfos: ExportDatasetInfo[] = [];
        const selectedWidgetInfos: ExportWidgetInfo[] = isGrid ? this.gridsSelectedWidgetsInfos : this.nonGridsSelectedWidgetsInfos;
        selectedWidgetInfos.forEach((widgetInfo) => {
            const widgetColumns = this.getWidgetColumns(widgetInfo.currentVisualizationConfig?.configs?.values);
            const dataTypeOverrides = this.getDataTypeOverrides(widgetInfo, metadata);
            if (!widgetInfo.isSubscribedToDashboardFilters) {
                let udfColumns: { colId: string | undefined, userDefinedFieldType: UserDefinedFieldType | undefined }[] = [];
                if (widgetInfo.currentVisualization === 'ADVANCED_GRID') {
                    udfColumns = widgetInfo.currentVisualizationConfig?.configs?.values
                        .filter((column) => this.exportService.isUserDefinedFieldColumn(column.value))
                        .map((column) => {
                            return { colId: column.value, userDefinedFieldType: column.datatype as UserDefinedFieldType };
                        }) ?? [];
                }
                let data = this.getWidgetData('widget', widgetInfo.id, udfColumns);
                const columns = this.getColumns(widgetInfo, isGrid, widgetColumns, metadata);
                const widgets = this.getWidgets(widgetInfo, columns, exportData ?? {}, dataTypeOverrides);
                if (this.exportService.shouldIncludeCrosstalkKeys(widgetInfo)) {
                    data = this.addCrosstalkKeysData(widgetInfo, data);
                    const id = widgetInfo.namedQueryId ?? '';
                    const crosstalkFields = this.crosstalkDataService.getCrosstalkSchemaFields(id);
                    exportDatasetInfos.push({ metadata: metadata[widgetInfo.id], widgets, data, summary: {}, crosstalkFields });
                } else {
                    exportDatasetInfos.push({ metadata: metadata[widgetInfo.id], widgets, data, summary: {} });
                }
                this.sequenceNo += 1;
            }
        });
        return exportDatasetInfos;
    }

    private getParamDatasets(
        widgetsMetadata: { [widgetId: number | string]: MetadataLookup },
        isGrid: boolean,
        exportData?: Record<string, ExportableData>,
    ): ExportDatasetInfo[] {
        // this mutates the parameter widgetsMetadata which is passed in as either this.gridsMetadata or this.nonGridsMetadata
        // additionally, at this point, this code works no matter which of the types for widgetsMetadata is passed in,
        // however, this.setDisplayName definitely expects the { [widgetId: number]: MetadataLookup } type, so we're going to force
        Object.values(widgetsMetadata ?? {}).forEach((metadata: MetadataLookup | FieldMetadata) => {
            Object.entries(metadata).forEach(([key, value]) => {
                value.name = key;
            });
        });

        if (exportData) {
            Object.keys(exportData).forEach((key) => {
                this.setDisplayName(key, exportData[key], widgetsMetadata);
            });
        }

        return [
            ...this.getSubscribedWidgetDatasets(widgetsMetadata, isGrid, exportData),
            ...this.getUnsubscribedWidgetDatasets(widgetsMetadata, isGrid, exportData),
        ];
    }

    private getGridsFullExportColumns(exportData: Record<string, ExportableData>): { widgetId: string, columns: string[] | undefined }[] {
        return Object.keys(exportData).map((fd) => {
            const exportDataColumns = exportData[fd].data.length ? Object.keys(exportData[fd].data[0]) : [];
            let gridColumns: (string | undefined)[];
            let columns: string[] | undefined;
            if (exportDataColumns.length) {
                const widgetInfo = this.gridsSelectedWidgetsInfos.find((widget) => widget.id?.toString() === fd.toString());
                gridColumns = widgetInfo?.currentVisualizationConfig?.configs?.values.map((value) => value.value) ?? [];
                // keep the column order saved in the grid configuration
                columns = exportDataColumns.filter((column) => gridColumns.includes(column));

                gridColumns.forEach((item, ind) => {
                    if (columns![ind] !== item) {
                        const currIndex = columns!.findIndex((col) => col === item);
                        if (currIndex !== -1) {
                            columns!.splice(ind, 0, columns!.splice(currIndex, 1)[0]);
                        }
                    }
                });
            }
            return {
                widgetId: fd,
                columns,
            };
        });
    }

    private async exportFullDashboard(exportData: Record<string, ExportableData>): Promise<void> {
        if (Object.keys(exportData).length !== this.selectedWidgetInfos.length) {
            return;
        }

        let gridsDataset;
        let nonGridsDataset;

        if (this.gridsSelectedWidgetsInfos.length) {
            const exportGridData: Record<string, ExportableData> = {};
            const gridIds = this.gridsSelectedWidgetsInfos.map((gridWidget) => gridWidget.id.toString());
            const exportDataKeys = Object.keys(exportData).filter((key) => gridIds.indexOf(key) !== -1);
            exportDataKeys.forEach((key) => {
                exportGridData[key] = exportData[key];
            });

            this.gridsFullExportColumns = this.getGridsFullExportColumns(exportGridData);
            gridsDataset = this.getParamDatasets(
                this.gridsMetadata as unknown as { [widgetId: number | string]: MetadataLookup },  // see comment in getParamDatasets
                true,
                exportData);
            this.requestParam?.dataSet.push(...gridsDataset);
        }

        if (this.nonGridsSelectedWidgetsInfos.length) {
            nonGridsDataset = this.getParamDatasets(
                this.nonGridsMetadata as unknown as { [widgetId: number | string]: MetadataLookup },   // see comment in getParamDatasets
                false,
                exportData);
            this.requestParam?.dataSet.push(...nonGridsDataset);
        }

        await this.exportData();
        this.filteredDataService.clearFullData({});
    }

    private async exportFilteredDashboard(exportData: Record<string, ExportableData>): Promise<void> {
        if (Object.keys(exportData).length !== this.selectedWidgetCount) {
            return;
        }

        this.requestParam = this.getExportRequestParams(exportData);
        await this.exportData();
        this.filteredDataService.clearFilteredData({});
    }

    private getExportRequestParams(exportData: Record<string, ExportableData> | undefined): ExportRequestParameter {
        const requestParam: ExportRequestParameter = {
            dashboardName: this.currentDashboard?.getExtraParameters()?.name ?? '',
            dashboardId: this.currentDashboard?.id.toString() ?? '',
            dataSet: [],
        };

        if (!exportData && this.exportFormat === 'PDF') {
            return requestParam;
        }

        requestParam.dataSet = Object.entries(exportData ?? {}).map(([widgetId, exportableDatum]) => {
            let columns = exportableDatum.data.length ? Object.keys(exportableDatum.data[0]) : [];
            columns = this.filterConversationIdColumn(columns);

            if (this.exportFormat === 'CSV' && columns.length) {
                columns = this.exportService.addDateColumnsForCSVExport(exportableDatum, widgetId, columns);
                columns = this.normalizeGrouperColumns(columns);
            }

            let dataTypeOverrides: Record<string, string | undefined> = {};
            let currentWidget: ExportWidgetInfo | undefined;
            const udfColumns: UdfColumn[] = [];
            if (this.gridsSelectedWidgetsInfos) {
                currentWidget = this.gridsSelectedWidgetsInfos.find((sw) => sw.id.toString() === widgetId);
                if (currentWidget) {
                    const widget = currentWidget;
                    currentWidget.currentVisualizationConfig?.configs?.values.forEach((column) => {
                        if (this.exportService.isUserDefinedFieldColumn(column.value)) {
                            udfColumns.push({
                                name: (column.customName ?? column.label) ?? '',
                                type: column.datatype as UserDefinedFieldType,
                            });
                        }
                        dataTypeOverrides = this.getDataTypeOverrides(
                            widget,
                            this.metadata as { [widgetId: number | string]: MetadataLookup },
                        );
                    });

                    if (udfColumns.length) {
                        udfColumns.forEach((udf) => {
                            const { name, type } = udf;
                            exportableDatum.data.forEach((datum) => {
                                datum[name] = this.exportService.setUserDefinedFieldExportValue(datum, name, type);
                            });
                        });
                    }
                }
            }

            const widgets = this.buildExportWidgets(
                widgetId,
                exportableDatum,
                columns,
                dataTypeOverrides,
                currentWidget,
                this.sequenceNo,
                this.exportFormat,
                this.isFullExport,
            );

            const metadata = this.setDisplayName(
                widgetId,
                exportableDatum,
                this.metadata as { [widgetId: number | string]: MetadataLookup },
                true,
            );
            this.sequenceNo += 1;

            if (this.exportService.shouldIncludeCrosstalkKeys(currentWidget)) {
                const queryId = currentWidget?.namedQueryId ?? '';
                return {
                    metadata,
                    widgets,
                    data: exportableDatum.data,
                    summary: exportableDatum.summary,
                    crosstalkFields: this.crosstalkDataService.getCrosstalkSchemaFields(queryId),
                };
            }

            return {
                metadata,
                widgets,
                data: exportableDatum.data,
                summary: exportableDatum.summary,
            };
        });

        return requestParam;
    }

    private exportInfosToMetadataFetchContexts(infos: ExportWidgetInfo[]): WidgetIdNamedQueryId[] {
        return infos.map((info) => new WidgetIdNamedQueryId(info.id, info.namedQueryId));
    }

    private setDisplayName(
        widgetId: number | string,
        data: ExportableData,
        metadata: { [widgetId: number | string]: MetadataLookup },
        returnFilteredMetadata = false,
    ): MetadataLookup | undefined {
        this.selectedWidgetInfos.forEach((widget) => {
            if (widget.name === data.widgetName) {
                this.managerService.getWidgetPreferences(widget.id)?.visualizationConfigs.forEach((vizConfig) => {
                    vizConfig.configs?.values.forEach((configValue, index) => {
                        const configValueValue = configValue.value;
                        if (configValueValue === undefined) {
                            return console.error(`DashboardExportComponent.setDisplayName: vizConfig.configs.values[${index}].value is undefined`);
                        }

                        const columnDefinition = metadata[widget.id]?.[configValueValue];
                        if (columnDefinition && !columnDefinition.nameChecked && !(configValue as GridConfigItem).isRanked) {
                            const displayName = configValue.showCustomName ? configValue.customName : configValue.label;
                            if (!displayName) {
                                return console.error(`DashboardExportComponent.setDisplayName: vizConfig.configs.values[${index}].value has no displayName or customName`);
                            }
                            columnDefinition.displayName = displayName;
                            columnDefinition.nameChecked = true;
                        }
                    });
                });
            }
        });

        if (metadata[widgetId]) {
            Object.keys(metadata[widgetId]).forEach((key) => {
                metadata[widgetId][key].nameChecked = false;
            });
        }

        if (returnFilteredMetadata) {
            return (this.metadata as { [widgetId: number | string]: MetadataLookup })?.[widgetId];
        }
    }

    private getWidgetData(
        sourceType: 'dashboard' | 'widget',
        namedQueryOrWidgetId: number | string,
        userDefinedFieldColumns: { colId: string | undefined, userDefinedFieldType: UserDefinedFieldType | undefined }[],
    ): WidgetData[] {
        const dataWrapper = this.widgetDatasource?.datasources.find((dw) => {
            return dw.uniqueKey.sourceType === sourceType && dw.uniqueKey.sourceId === namedQueryOrWidgetId;
        });

        const originalData = dataWrapper?.originalData;
        const data = dataWrapper?.data;
        const widgetData = deepClone(originalData ?? data ?? []);

        if (userDefinedFieldColumns.length) {
            userDefinedFieldColumns.forEach((udf) => {
                const { colId, userDefinedFieldType: type } = udf;
                if (colId && type) {
                    widgetData.forEach((datum) => datum[colId] = this.exportService.setUserDefinedFieldExportValue(datum, colId, type));
                }
            });
        }

        return widgetData;
    }

    private getDataTypeOverrides(
        widgetInfo: ExportWidgetInfo,
        metadata: { [widgetId: number | string]: MetadataLookup },
    ): { [key: string]: string | undefined } {
        const widgetId = widgetInfo.id;
        if (!widgetInfo) {
            return {};
        }

        const dataTypeOverrides: Record<string, string | undefined> = {};
        widgetInfo.currentVisualizationConfig?.configs?.values.forEach((column) => {
            if (column.colId !== crosstalkCheckboxFieldId) {
                const columnName = column.value ?? '';
                const columnDefinition = metadata[widgetId][columnName];
                if (columnName === columnDefinition?.name && column.displayType !== columnDefinition.displayType) {
                    dataTypeOverrides[column.label ?? ''] = column.displayType;
                } else if (column.displayType === 'string') {
                    dataTypeOverrides[column.label ?? ''] = column.displayType;
                }
            }
        });
        return dataTypeOverrides;
    }

    private getWidgetColumns(values: ValueConfigItem[] | undefined): string[] {
        if (values) {
            return values.reduce((columns: string[], column: ValueConfigItem) => {
                if (column.value != null) {
                    columns.push(column.value);
                }
                return columns;
            }, []);
        }
        return [];
    }

    private getColumns(
        widgetInfo: ExportWidgetInfo,
        isGrid: boolean,
        widgetColumns: (string | undefined)[],
        metadata: { [widgetId: number | string]: MetadataLookup },
    ): string[] {
        const gridColumnsForWidget = this.gridsFullExportColumns?.length ?
            this.gridsFullExportColumns.find((widget) => widget.widgetId.toString() === widgetInfo.id.toString()) :
            null;
        const columns = !isGrid ?
            Object.keys(metadata[widgetInfo.id]) :
            (gridColumnsForWidget ? (gridColumnsForWidget.columns! || widgetColumns) : []);

        if (this.exportFormat === 'CSV' && columns.length) {
            if (['Recon', 'Checklists'].indexOf(widgetInfo.namedQuery.normalizeQueryTypeName() ?? '') === -1) {
                columns.push('From Date');
            }
            columns.push(...['To Date', 'Time of Extract']);
        }

        if (this.exportService.shouldIncludeCrosstalkKeys(widgetInfo)) {
            const id = widgetInfo.namedQueryId ?? '';
            this.crosstalkDataService.getCrosstalkSchemaFields(id)?.forEach((field) => {
                columns.push(field.name);
            });
        }
        return columns;
    }

    private getWidgets(
        widgetInfo: ExportWidgetInfo,
        columns: string[],
        exportData: Record<string, ExportableData>,
        dataTypeOverrides: { [key: string]: string | undefined },
    ): ExportDatasetWidgetInfo[] {
        return [{
            widgetId: widgetInfo.id.toString(),
            widgetName: `${this.sequenceNo}_${widgetInfo.name}-${ExportType.FULL}`,
            columnNames: columns,
            queryType: widgetInfo.namedQuery.normalizeQueryTypeName(),
            extractionDateTime: DdvDateTime.now.toShortDateAndTime(),
            dateFrom: exportData[widgetInfo.id] ?
                this.exportService.fuzzyDateCheck(exportData[widgetInfo.id].startDate, true) :
                undefined,
            dateTo: exportData[widgetInfo.id] ?
                this.exportService.fuzzyDateCheck(exportData[widgetInfo.id].endDate) :
                undefined,
            dataTypeOverrides,
        }];
    }

    private addCrosstalkKeysData(widgetInfo: ExportWidgetInfo, data: WidgetData[]): WidgetData[] {
        const crosstalkFields = this.crosstalkDataService.getCrosstalkSchemaFields(widgetInfo.namedQueryId ?? '') ?? [];
        data.forEach((row) => {
            crosstalkFields.forEach((field) => row[field.name] = row[field.displayName]?.toString() ?? '');
        });
        return data;
    }

    private moveConversationIdAsLastColumn(): void {
        this.requestParam?.dataSet.forEach((dataset) => {
            if (dataset.crosstalkFields) {
                this.requestParam?.dataSet.forEach((exportInfo) => {
                    exportInfo.widgets.forEach((widget) => {
                        widget.columnNames = widget.columnNames.filter((column) => column !== 'conversationId');
                        widget.columnNames.push('conversationId');
                    });
                    exportInfo.crosstalkFields = exportInfo.crosstalkFields?.filter((field) => field.name !== 'conversationId');
                    exportInfo.crosstalkFields?.push({ name: 'conversationId', displayName: 'conversationId' });
                });
            }
        });
    }

    private filterConversationIdColumn(columns: string[]): string[] {
        return this.includeCrosstalkKeys ?
            columns.filter((column) => column !== 'Conversation Id') :
            columns.filter((column) => column !== 'conversationId');
    }

    private normalizeGrouperColumns(columns: string[]): string[] {
        return columns.map((column) => {
            if (column.includes('__Grouper__')) {
                return column.substring(0, column.indexOf('__Grouper__') - 1);
            }
            return column;
        });
    }

    private buildExportWidgets(
        widgetId: string | number,
        exportableDatum: ExportableData,
        columns: string[],
        dataTypeOverrides: Record<string, string | undefined>,
        currentWidget?: ExportWidgetInfo,
        sequenceNo?: number,
        exportFormat?: ExportFormat,
        isFullExport?: boolean,
    ): ExportDatasetWidgetInfo[] {
        const widgetNameSuffix = isFullExport ? ExportType.FULL : ExportType.FILTERED;
        return [
            {
                widgetId: widgetId.toString(),
                widgetName: `${sequenceNo ?? 1}_${exportableDatum.widgetName}-${widgetNameSuffix}`,
                columnNames: !isFullExport && exportFormat === 'EXCEL' && exportableDatum.visibleColumns?.length ?
                    exportableDatum.visibleColumns :
                    columns,
                queryType: exportableDatum.queryType,
                dateFrom: exportableDatum ? this.exportService.fuzzyDateCheck(exportableDatum.startDate, true) : undefined,
                dateTo: exportableDatum ? this.exportService.fuzzyDateCheck(exportableDatum.endDate) : undefined,
                extractionDateTime: DdvDateTime.now.toShortDateAndTime(),
                groupers: exportableDatum.groupers ?? [],
                dataTypeOverrides,
                showSubTotal: currentWidget?.currentVisualizationConfig?.showSubTotal,
            },
        ];
    }

    private fullExport(dashboardName: string, datasets: ExportDatasetInfo[], exportFormat: ExportFormat): void {
        this.dashboardExportService.exportFullDashboard(
            datasets,
            dashboardName,
            this.exportWidgetInfos,
            exportFormat,
            this.userPreferences?.clientCode ?? '',
        );
    }

    private filteredExport(dashboardName: string, datasets: ExportDatasetInfo[], exportFormat: ExportFormat): void {
        this.dashboardExportService.exportFilteredDashboard(
            datasets,
            dashboardName,
            this.exportWidgetInfos,
            exportFormat,
            this.userPreferences?.clientCode ?? '',
        );
    }
}

function setExportDatasetInfoForExportWithCrosstalkKeys(dataset: ExportDatasetInfo): ExportDatasetInfo {
    if (dataset.widgets && dataset.metadata) {
        const datasetMetadata = dataset.metadata;
        Object.keys(datasetMetadata).forEach((key) => {
            dataset.widgets.forEach((widget) => {
                const widgetGroupers = widget.groupers ?? [];
                widget.groupers = widgetGroupers.map((grouperName) => {
                    if (datasetMetadata[key].displayName === grouperName) {
                        return datasetMetadata[key].name;
                    }
                    return grouperName;
                });

                widget.columnNames = widget.columnNames.map((columnName) => {
                    if (columnName.endsWith('_crosstalk_key')) {
                        return columnName;
                    }
                    return datasetMetadata[key].displayName === columnName ? datasetMetadata[key].name : columnName;
                });
            });

            if (dataset.data) {
                dataset.data = dataset.data.map((data) => {
                    const newData = Object.keys(data).reduce((datum, dataKey) => {
                        if (datasetMetadata[key].displayName === dataKey) {
                            datum[datasetMetadata[key].name] = data[dataKey];
                        } else {
                            datum[dataKey] = data[dataKey];
                        }
                        return datum;
                    }, {} as Record<string, unknown>);

                    newData.conversationId = data.conversationId ?? '';

                    return newData;
                });
            }
            datasetMetadata[key].displayName = datasetMetadata[key].name;
        });
        dataset.metadata = datasetMetadata;
    }

    return dataset;
}
